/**
 * Page previews reach the renderer as `cp-preview://page/<segments>?v=…[&size=thumbnail]`.
 *
 * They used to arrive as `data:` URLs. Chromium keeps every `data:` image a page
 * has shown — the multi-megabyte base64 string and the resource built from it —
 * for the life of the document: a 600 dpi colour page cost the renderer ~2.3 MB
 * that no GC, memory-pressure signal or job switch gave back, so a long scan
 * session grew the window past a gigabyte (TWAIN soak on 6.2.35, 151 → 1,113 MB
 * over 410 pages). An ordinary URL-backed image is evictable like any web image.
 *
 * The segments are the preview's path *relative to the work root*, each one
 * percent-encoded. Parsing is the security boundary's first half and refuses
 * anything that is not a plain file name (`..`, `.`, empty, separators, drive
 * colons, control characters); the main process then resolves the result inside
 * the work root and re-checks containment on the real path.
 */
export const PAGE_PREVIEW_SCHEME = 'cp-preview';
const host = 'page';
const maxSegments = 32;
const previewExtensions = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.bmp',
  '.webp',
  '.tif',
  '.tiff',
]);

export type PagePreviewPurpose = 'full' | 'thumbnail';
export type PagePreviewRequest = {
  segments: string[];
  purpose: PagePreviewPurpose;
  version: string;
};

const unsafeSegment = /[\\/:\u0000-\u001f\u007f]/;
const safeSegment = (segment: string) =>
  segment.length > 0 &&
  segment.length <= 255 &&
  segment !== '.' &&
  segment !== '..' &&
  !unsafeSegment.test(segment) &&
  // Windows quietly drops trailing dots and spaces ("a.. " is "a"); refuse
  // names whose meaning on disk differs from their meaning here.
  !/[. ]$/.test(segment);
const safeVersion = /^[0-9a-z.-]{1,64}$/i;

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot).toLowerCase();
};

export function isPreviewFileName(name: string) {
  return previewExtensions.has(extensionOf(name));
}

/** Build the URL for a preview whose path relative to the work root is `segments`. */
export function pagePreviewUrl(
  segments: readonly string[],
  purpose: PagePreviewPurpose,
  version: string,
) {
  if (
    segments.length < 1 ||
    segments.length > maxSegments ||
    !segments.every(safeSegment) ||
    !isPreviewFileName(segments[segments.length - 1]!) ||
    !safeVersion.test(version)
  )
    throw new Error('Invalid page preview request.');
  const query = new URLSearchParams({ v: version });
  if (purpose === 'thumbnail') query.set('size', 'thumbnail');
  return `${PAGE_PREVIEW_SCHEME}://${host}/${segments.map(encodeURIComponent).join('/')}?${query}`;
}

/** Parse a request URL, or null for anything this scheme does not serve. */
export function parsePagePreviewUrl(value: string): PagePreviewRequest | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (
    url.protocol !== `${PAGE_PREVIEW_SCHEME}:` ||
    url.hostname !== host ||
    url.port ||
    url.username ||
    url.password ||
    url.hash
  )
    return null;
  const raw = url.pathname.split('/').slice(1);
  if (raw.length < 1 || raw.length > maxSegments) return null;
  const segments: string[] = [];
  for (const part of raw) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(part);
    } catch {
      return null;
    }
    if (!safeSegment(decoded)) return null;
    segments.push(decoded);
  }
  if (!isPreviewFileName(segments[segments.length - 1]!)) return null;
  const keys = [...url.searchParams.keys()];
  if (keys.some((key) => key !== 'v' && key !== 'size') || new Set(keys).size !== keys.length)
    return null;
  const version = url.searchParams.get('v');
  const size = url.searchParams.get('size');
  if (!version || !safeVersion.test(version)) return null;
  if (size !== null && size !== 'thumbnail') return null;
  return { segments, purpose: size === 'thumbnail' ? 'thumbnail' : 'full', version };
}

/** What the renderer accepts as a preview source: our scheme, or a legacy `data:` raster. */
export function isPagePreviewSource(value: string) {
  return (
    parsePagePreviewUrl(value) !== null ||
    /^data:image\/(?:png|jpeg|gif|bmp|webp|tiff);base64,[A-Za-z0-9+/=\r\n]+$/.test(value)
  );
}
